import type {ChangeEvent, FormEvent} from 'react';
import React, {useState} from 'react';
import './ContactForm.css';
import {getCsrfToken} from "../App";

export type ContactFormData = {
    contactName: string;
    contactEmail: string;
    subject: string;
    message: string;
    webAppContent: string;
};

const SUBJECT_OPTIONS: string[] = [
    'Please Choose...',
    'Nutricherry Site - General enquiry',
    'Nutricherry Site - Report a bug, data, or recipe problem',
    'Nutricherry Site - Feature request',
    'TheCodersCorner/TcMenu - Production support',
    'TheCodersCorner/TcMenu - Consultancy or Training',
    'Other',
];

export function ContactForm(): React.JSX.Element {
    const [form, setForm] = useState<ContactFormData>({
        contactName: '',
        contactEmail: '',
        subject: SUBJECT_OPTIONS[0] as string,
        message: '',
        webAppContent: ''
    });
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const {name, value} = e.target;
        setForm((prev) => ({...prev, [name]: value}));
    };

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();

        if (form.message.length === 0 || form.message.length > 1000) {
            alert("Please enter a message with 1000 characters or less");
            return;
        }
        if (form.subject === SUBJECT_OPTIONS[0]) {
            alert("Please select a subject");
            return;
        }
        if (form.contactName.length === 0 || form.contactName.length > 64) {
            alert("Please enter a name with 64 characters or less");
            return;
        }

        getCsrfToken().then((token: string) => {
            fetch('/api/v1/formContact/send', {
                method: 'POST',
                body: JSON.stringify(form),
                headers: {"Content-Type": "application/json", "Accept": "application/json", "X-XSRF-TOKEN": token}
            }).then(response => {
                if (!response.ok) {
                    setError("An error occurred while sending your message. Please try again later.");
                    setSubmitted(false);
                }
                return response.text();
            }).then(response => {
                if (response.trim() === "true") {
                    setSubmitted(true);
                    setError(null);
                    setForm({
                        contactName: '',
                        contactEmail: '',
                        subject: SUBJECT_OPTIONS[0] as string,
                        message: '',
                        webAppContent: ''
                    });
                } else {
                    setError("An error occurred while sending your message. Please try again later.");
                    setSubmitted(false);
                }
            });
        });

    };

    return (
        <div className="container" style={{maxWidth: 700}}>
            <h1>Contact us</h1>
            {error && <div className="error-message">{error}</div>}
            {submitted ? (
                <div
                    style={{
                        padding: '12px 16px',
                        background: '#e7f5ff',
                        border: '1px solid #a5d8ff',
                        borderRadius: 6,
                        marginBottom: 16,
                    }}
                >
                    Thanks for your message. We'll get back to you as soon as possible.
                </div>
            ) : null}

            <form onSubmit={onSubmit}>
                <div className="form-row" style={{marginBottom: 12}}>
                    <label htmlFor="contactName" style={{display: 'block', marginBottom: 4}}>
                        Name
                    </label>
                    <input
                        id="contactName"
                        name="contactName"
                        type="text"
                        value={form.contactName}
                        onChange={onChange}
                        required
                        style={{width: '100%', padding: 8}}
                        placeholder="Your name"
                    />
                </div>

                <div className="form-row" style={{marginBottom: 12}}>
                    <label htmlFor="contactEmail" style={{display: 'block', marginBottom: 4}}>
                        Email address
                    </label>
                    <input
                        id="contactEmail"
                        name="contactEmail"
                        type="email"
                        value={form.contactEmail}
                        onChange={onChange}
                        required
                        style={{width: '100%', padding: 8}}
                        placeholder="you@example.com"
                    />
                </div>

                <div className="form-row" style={{marginBottom: 12}}>
                    <label htmlFor="subject" style={{display: 'block', marginBottom: 4}}>
                        Subject
                    </label>
                    <select
                        id="subject"
                        name="subject"
                        value={form.subject}
                        onChange={onChange}
                        required
                        style={{width: '100%', padding: 8}}
                    >
                        {SUBJECT_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                                {opt}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-row" style={{marginBottom: 12}}>
                    <label htmlFor="message" style={{display: 'block', marginBottom: 4}}>
                        Message
                    </label>
                    <textarea
                        id="message"
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        required
                        rows={6}
                        style={{width: '100%', padding: 8, resize: 'vertical'}}
                        placeholder="How can we help?"
                    />
                </div>
                <input name="webAppContent" value="" onChange={onChange} style={{display: "none"}}/>

                <button type="submit">Send</button>
            </form>
        </div>
    );
}

export default ContactForm;
